import React, {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import type { Location } from "react-router-dom";
import "./App.css";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { ThemeProvider } from "./context/ThemeContext";
import { NotificationProvider } from "./context/NotificationContext";
import { WatchHistoryProvider } from "./context/WatchHistoryContext";
import { RecommendationProvider } from "./context/RecommendationContext";
import { getAllVideos, getVideoById } from "./api/videos";
import ProtectedRoute from "./routes/ProtectedRoute";
import ErrorToasts from "./components/common/ErrorToasts";
import { ADMIN_ROUTES, ADMIN_BASE_PATH } from "./config/adminRoutes";
import { useNavigationHelpers } from "./hooks/useNavigationHelpers";
import { AuthProvider as OidcProvider, useAuth } from "react-oidc-context";
import { AuthProvider } from "./context/AuthContext";
import oidcConfig from "./config/oidc";
import { SpryAuthProvider } from "spry-apps-dropdown";
import { useSocket } from "./hooks/useSocket";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

const ForYouFeed = lazy(() => import("./components/video/ForYouFeed"));
const Navbar = lazy(() => import("./components/navigation/Navbar"));
const Sidebar = lazy(() => import("./components/navigation/Sidebar"));
const SystemNotificationBanner = lazy(
  () => import("./components/common/SystemNotificationBanner"),
);
const Explore = lazy(() => import("./components/feed/Explore"));
const SearchPage = lazy(() => import("./components/feed/SearchPage"));
const Following = lazy(() => import("./components/feed/Following"));
const StoryViewer = lazy(() => import("./components/stories/StoryViewer"));
const StoryCreator = lazy(() => import("./components/stories/StoryCreator"));
const Live = lazy(() => import("./components/live/LIVE"));
const WatchLater = lazy(() => import("./components/playlist/WatchLater"));
const PlaylistsManager = lazy(
  () => import("./components/playlist/PlaylistsManager"),
);
const VideoQAInbox = lazy(() => import("./components/video/VideoQAInbox"));
const UploadPage = lazy(() => import("./components/upload/UploadPage"));
const Profile = lazy(() => import("./components/profile/Profile"));
const More = lazy(() => import("./components/common/More"));
const SpryclipsStudio = lazy(() => import("./components/creator/TikTokStudio"));
const EffectsLab = lazy(() => import("./components/creator/EffectsLab"));
const SellerDashboard = lazy(
  () => import("./components/business/SellerDashboard"),
);
const GrowthToolsHub = lazy(
  () => import("./components/creator/GrowthToolsHub"),
);
const SupportCenter = lazy(() => import("./components/common/SupportCenter"));
const BusinessSuite = lazy(() => import("./components/business/BusinessSuite"));
const AdManager = lazy(() => import("./components/business/AdManager"));
const ProductCatalog = lazy(
  () => import("./components/business/ProductCatalog"),
);
const BusinessMessaging = lazy(
  () => import("./components/business/BusinessMessaging"),
);
const NotificationCenter = lazy(
  () => import("./components/social/NotificationCenter"),
);
const NotificationSettings = lazy(
  () => import("./components/settings/NotificationSettings"),
);
const PrivacySettings = lazy(
  () => import("./components/settings/PrivacySettings"),
);
const Balance = lazy(() => import("./components/common/Balance"));
const MessageCenter = lazy(() => import("./components/social/MessageCenter"));
const AdminPanel = lazy(() => import("./components/admin/AdminPanel"));
const AdminLogin = lazy(() => import("./components/admin/AdminLogin"));
const PolicyCompliance = lazy(
  () => import("./components/admin/PolicyCompliance"),
);
const SecuritySettings = lazy(
  () => import("./components/admin/SecuritySettings"),
);
const NotificationsControl = lazy(
  () => import("./components/admin/NotificationsControl"),
);

interface VideoUserProfile {
  avatar?: string;
}

interface VideoUser {
  username?: string;
  profile?: VideoUserProfile;
  [key: string]: unknown;
}

export interface ProcessedVideo {
  _id: string;
  quality: string;
  url: string;
  path: string;
}

export interface VideoData {
  _id?: string;
  id?: string;
  url?: string;
  videoUrl?: string;
  thumbnail?: string;
  user?: VideoUser;
  avatar?: string;
  description?: string;
  song?: string;
  likes?: number;
  comments?: number;
  shares?: number;
  views?: number;
  isLiked?: boolean;
  isSaved?: boolean;
  isFollowing?: boolean;
  processedVideos?: ProcessedVideo[];
  [key: string]: unknown;
}

interface FeedItem {
  id: string;
  src: string;
  poster?: string;
  profileImg?: string;
  username?: string;
  description?: string;
  hashtags?: string[];
  song?: string;
  likes?: number;
  comments?: number;
  shares?: number;
  views?: number;
  isLiked?: boolean;
  isSaved?: boolean;
  video: VideoData;
}

const normalizeVideoData = (video: Record<string, unknown>): VideoData => {
  const candidateId =
    video._id ??
    video.id ??
    video.videoId ??
    (typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const username =
    (video.user as VideoUser | undefined)?.username ||
    (video.channel as string | undefined) ||
    "@user";

  const avatar =
    (video.user as VideoUser | undefined)?.profile?.avatar ||
    (video.avatar as string | undefined) ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=ff0050&color=fff`;

  return {
    _id: String(candidateId),
    id: String(candidateId),
    url: (video.url as string) || (video.videoUrl as string) || "",
    videoUrl: (video.videoUrl as string) || (video.url as string) || "",
    thumbnail: video.thumbnail as string | undefined,
    user:
      (video.user as VideoUser | undefined) ||
      ({
        username,
        profile: {
          avatar,
        },
      } as VideoUser),
    avatar,
    description: (video.description as string) || "",
    song:
      (video.song as string) ||
      ((video as any)?.music?.name as string) ||
      ((video as any)?.metadata?.music?.name as string) ||
      "Original Sound",
    music: video.music as
      | { name?: string; artist?: string; duration?: string; audioUrl?: string }
      | undefined,
    metadata: video.metadata as Record<string, unknown> | undefined,
    likes: typeof video.likes === "number" ? video.likes : 0,
    saves: typeof video.saves === "number" ? video.saves : 0,
    comments: typeof video.comments === "number" ? video.comments : 0,
    shares: typeof video.shares === "number" ? video.shares : 0,
    views: typeof video.views === "number" ? video.views : 0,
    hashtags: video.hashtags as string[] | undefined,
    channel: video.channel as string | undefined,
    isLiked: Boolean(video.isLiked),
    isSaved: Boolean(video.isSaved || video.isBookmarked),
    isFollowing: video.isFollowing as boolean | undefined,
    processedVideos: Array.isArray(video.processedVideos)
      ? (video.processedVideos as ProcessedVideo[])
      : [],
  };
};

const toFeedItem = (video: VideoData): FeedItem => {
  const fallbackId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const id = String(video._id || video.id || fallbackId);
  const username =
    video.user?.username || (video as { channel?: string }).channel || "@user";
  const avatar =
    video.user?.profile?.avatar ||
    (video as { avatar?: string }).avatar ||
    `https://ui-avatars.com/api/?name=${username}&background=ff0050&color=fff`;
  const videoUrl = (video.url as string) || (video.videoUrl as string) || "";
  const thumbnailUrl = video.thumbnail;

  return {
    id,
    src: videoUrl,
    poster: thumbnailUrl || undefined,
    profileImg: avatar,
    username,
    description: video.description || "",
    hashtags: video.hashtags as string[] | undefined,
    song: (video.song as string) || "Original Sound",
    likes: video.likes || 0,
    comments: video.comments || 0,
    shares: video.shares || 0,
    views: video.views || 0,
    isLiked: video.isLiked,
    isSaved: video.isSaved,
    video: {
      ...video,
      _id: id,
      id: id,
    },
  };
};

const LoadingSpinner: React.FC = () => (
  <div
    className="loading-container fade-in"
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      flexDirection: "column",
      gap: "1.5rem",
      background: "#ffffff",
    }}
  >
    <div
      className="loading-spinner"
      style={{
        width: "50px",
        height: "50px",
        border: "4px solid rgba(0, 0, 0, 0.1)",
        borderTop: "4px solid #656565",
        borderRadius: "50%",
        animation: "spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite",
      }}
    ></div>
    <p
      style={{
        color: "#656565",
        fontSize: "16px",
        fontWeight: "500",
        animation: "pulse 2s ease-in-out infinite",
      }}
    >
      {(i18next.t as Function)('common.loadingContent', 'Loading amazing content...')}
    </p>
    <style>
      {`@keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .loading-container {
        transition: opacity 0.3s ease;
      }`}
    </style>
  </div>
);

const GlobalNotificationListener: React.FC = () => {
  const { socket, isConnected } = useSocket();
  const { user } = useAuth();

  useEffect(() => {
    if (!socket || !isConnected || !user) return;

    if ('Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }

    const handleGlobalMessage = (data: any) => {
      const { message } = data;
      const senderId = message?.sender?._id || message?.sender?.id || message?.sender;
      const currentUserId = (user as any)?._id || user?.profile?.sub;

      if (senderId && senderId !== currentUserId) {
        if ('Notification' in window && Notification.permission === 'granted') {
          const notificationBody = message.content?.text || 'Sent an attachment';
          const senderName = message.sender?.displayName || message.sender?.username || 'New Message';
          
          new Notification(senderName, { body: notificationBody });
          
          try {
            new Audio('/notification.mp3').play().catch(() => {});
          } catch (e) {}
        }
      }
    };

    (socket as any).on('message:received', handleGlobalMessage);

    return () => {
      (socket as any).off('message:received', handleGlobalMessage);
    };
  }, [socket, isConnected, user]);

  return null;
};

const SingleVideoFeed: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [videoItems, setVideoItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      if (!id) return;
      try {
        setLoading(true);

        const [targetVideoData, allVideosData] = await Promise.all([
          getVideoById(id).catch((e) => null),
          getAllVideos().catch((e) => []),
        ]);

        if (!targetVideoData) {
          setError("Video not found");
          setLoading(false);
          return;
        }

        const normalizedTarget = normalizeVideoData(
          targetVideoData as Record<string, unknown>,
        );
        const targetFeedItem = toFeedItem(normalizedTarget);

        let remainingFeedItems: FeedItem[] = [];
        if (Array.isArray(allVideosData)) {
          remainingFeedItems = allVideosData
            .map((v) => normalizeVideoData(v as Record<string, unknown>))
            .filter(
              (v) => String(v._id || v.id) !== String(normalizedTarget.id),
            )
            .map((v) => toFeedItem(v));
        }

        setVideoItems([targetFeedItem, ...remainingFeedItems]);
      } catch (e) {
        console.error("Error fetching feed:", e);
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  if (loading) return <LoadingSpinner />;

  if (error || videoItems.length === 0) {
    return (
      <div
        className="feed-error"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <p>{error || "Video unavailable"}</p>
      </div>
    );
  }

  return <ForYouFeed items={videoItems} />;
};

const HomeFeed: React.FC = () => {
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const fetchErrorLoggedRef = useRef(false);

  useEffect(() => {
    const handleVideoUploaded = async (event: Event) => {
      try {
        setLoading(true);
        const data = await getAllVideos();

        if (data && Array.isArray(data) && data.length > 0) {
          const normalizedVideos = data.map((video) =>
            normalizeVideoData(video as Record<string, unknown>),
          );
          setVideos(normalizedVideos);
        } else {
          console.warn("No videos returned after upload");
        }
        setError(null);
      } catch (err) {
        console.error("Error refreshing videos after upload:", err);
        setError("Failed to refresh feed. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener("videoUploaded", handleVideoUploaded);
    return () => {
      window.removeEventListener("videoUploaded", handleVideoUploaded);
    };
  }, [setVideos]);

  useEffect(() => {
    let isMounted = true;
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllVideos();
        if (!isMounted) return;
        if (Array.isArray(data) && data.length > 0) {
          setVideos(
            data.map((video) =>
              normalizeVideoData(video as Record<string, unknown>),
            ),
          );
        }
      } catch (err) {
        if (!isMounted) return;
        if (!fetchErrorLoggedRef.current) {
          console.warn("Failed to load videos, using fallback data:", err);
          fetchErrorLoggedRef.current = true;
        }
        setError("Failed to load videos");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchVideos();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleSearchChange = (event: CustomEvent<string>) => {
      const term = String(event.detail || "").trim();
      setSearchTerm(term);
    };

    window.addEventListener("searchTermChange" as any, handleSearchChange);
    document.addEventListener("searchTermChange" as any, handleSearchChange);

    return () => {
      window.removeEventListener("searchTermChange" as any, handleSearchChange);
      document.removeEventListener(
        "searchTermChange" as any,
        handleSearchChange,
      );
    };
  }, []);

  const featuredVideos: FeedItem[] = useMemo(() => {
    if (!videos || videos.length === 0) return [];
    const sorted = [...videos].sort((a, b) => {
      const engagementA = (a.likes || 0) + (a.views || 0);
      const engagementB = (b.likes || 0) + (b.views || 0);
      return engagementB - engagementA;
    });
    const topVideos = sorted.slice(0, 5);

    return topVideos.map((video) => toFeedItem(video));
  }, [videos]);

  const feedItems: FeedItem[] = useMemo(() => {
    if (!videos || videos.length === 0) {
      return [];
    }

    const featuredIds = new Set(featuredVideos.map((v) => v.id));
    let filteredVideos = videos;

    if (!searchTerm.trim()) {
      filteredVideos = videos.filter(
        (v) => !featuredIds.has(String(v._id || v.id)),
      );
    } else {
      filteredVideos = videos;
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();

      let matchCount = 0;
      filteredVideos = filteredVideos.filter((video) => {
        const description = String(video.description || "").toLowerCase();
        const username = String(
          video.user?.username || (video as { channel?: string }).channel || "",
        ).toLowerCase();
        const hashtags = Array.isArray(video.hashtags)
          ? video.hashtags.map((h) => String(h).toLowerCase()).join(" ")
          : String(video.hashtags || "").toLowerCase();
        const song = String(video.song || "").toLowerCase();
        const title = String(
          (video as { title?: string }).title || "",
        ).toLowerCase();

        const allSearchableText =
          `${description} ${username} ${hashtags} ${song} ${title}`.toLowerCase();

        const matches =
          description.includes(searchLower) ||
          username.includes(searchLower) ||
          hashtags.includes(searchLower) ||
          song.includes(searchLower) ||
          title.includes(searchLower) ||
          allSearchableText.includes(searchLower);

        if (matches) matchCount++;
        return matches;
      });
    }

    return filteredVideos.map((video) => toFeedItem(video));
  }, [videos, searchTerm, featuredVideos]);

  const allFeedItems: FeedItem[] = useMemo(() => {
    if (searchTerm.trim()) {
      return feedItems;
    }
    return [...featuredVideos, ...feedItems];
  }, [featuredVideos, feedItems, searchTerm]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && allFeedItems.length === 0) {
    return (
      <div className="feed-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <>
      <ForYouFeed items={allFeedItems} />
    </>
  );
};

const SearchResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const query = queryParams.get("q") || "";

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <SearchPage initialQuery={query} />
    </Suspense>
  );
};

const ExplorePage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Explore mode="overlay" onClose={() => navigate("/", { replace: true })} />
  );
};

const FollowingPage: React.FC = () => {
  return <Following />;
};

const LivePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const fromCreatorTools = location.state?.from === "creator-tools";

  return (
    <Live
      joinedRoomId={id}
      onClose={() => {
        if (fromCreatorTools) {
          sessionStorage.setItem("openCreatorTools", "true");
          navigate("/profile", {
            replace: true,
            state: { openCreatorTools: true },
          });
        } else {
          navigate("/");
        }
      }}
    />
  );
};

const WatchLaterPage: React.FC = () => {
  return <WatchLater />;
};

const PlaylistsPage: React.FC = () => {
  return <PlaylistsManager />;
};

const VideoQAPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <VideoQAInbox
      mode="overlay"
      onClose={() => navigate("/", { replace: true })}
    />
  );
};

const UploadPageRoute: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromCreatorTools =
    location.state?.from === "creator-tools" ||
    document.referrer.includes("profile") ||
    sessionStorage.getItem("fromCreatorTools") === "true";

  React.useEffect(() => {
    if (location.state?.from === "creator-tools") {
      sessionStorage.setItem("fromCreatorTools", "true");
    }
  }, [location.state]);

  const handleClose = () => {
    if (fromCreatorTools) {
      sessionStorage.setItem("openCreatorTools", "true");
      navigate("/profile", {
        replace: true,
        state: { openCreatorTools: true },
      });
    } else {
      sessionStorage.removeItem("openCreatorTools");
      navigate("/");
    }
  };

  return <UploadPage onClose={handleClose} />;
};

const ProfilePage: React.FC = () => {
  const { goBack } = useNavigationHelpers();
  const { username } = useParams<{ username?: string }>();
  return <Profile onClose={() => goBack()} username={username} />;
};

const MorePage: React.FC = () => {
  return <Navigate to="/" replace />;
};

const SpryclipsStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromCreatorTools = location.state?.from === "creator-tools";

  return (
    <SpryclipsStudio
      onClose={() => {
        if (fromCreatorTools) {
          navigate("/profile", { replace: true });
        } else {
          navigate("/profile");
        }
      }}
    />
  );
};

const EffectsLabPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <EffectsLab
      onClose={() => {
        navigate("/more", { replace: true });
      }}
    />
  );
};

const SellerDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  return <SellerDashboard onClose={() => navigate("/profile")} />;
};

const GrowthToolsPage: React.FC = () => {
  const navigate = useNavigate();
  return <GrowthToolsHub onClose={() => navigate("/more")} />;
};

const BusinessSuitePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromBusinessTools = location.state?.from === "business-tools";

  return (
    <BusinessSuite
      onClose={() => {
        if (fromBusinessTools) {
          sessionStorage.setItem("openBusinessTools", "true");
          navigate("/profile", {
            replace: true,
            state: { openBusinessTools: true },
          });
        } else {
          navigate("/profile");
        }
      }}
    />
  );
};

const AdManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromBusinessTools = location.state?.from === "business-tools";

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AdManager
        onClose={() => {
          if (fromBusinessTools) {
            sessionStorage.setItem("openBusinessTools", "true");
            navigate("/profile", {
              replace: true,
              state: { openBusinessTools: true },
            });
          } else {
            navigate("/profile");
          }
        }}
      />
    </Suspense>
  );
};

const ProductCatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromBusinessTools = location.state?.from === "business-tools";

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ProductCatalog
        onClose={() => {
          if (fromBusinessTools) {
            sessionStorage.setItem("openBusinessTools", "true");
            navigate("/profile", {
              replace: true,
              state: { openBusinessTools: true },
            });
          } else {
            navigate("/profile");
          }
        }}
      />
    </Suspense>
  );
};

const BusinessMessagingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromBusinessTools = location.state?.from === "business-tools";

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <BusinessMessaging
        onClose={() => {
          if (fromBusinessTools) {
            sessionStorage.setItem("openBusinessTools", "true");
            navigate("/profile", {
              replace: true,
              state: { openBusinessTools: true },
            });
          } else {
            navigate("/profile");
          }
        }}
      />
    </Suspense>
  );
};

const ActivityCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPersonalTools = location.state?.from === "personal-tools";

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <NotificationCenter
        onClose={() => {
          if (fromPersonalTools) {
            sessionStorage.setItem("openPersonalTools", "true");
            navigate("/profile", {
              replace: true,
              state: { openPersonalTools: true },
            });
          } else {
            navigate("/profile");
          }
        }}
      />
    </Suspense>
  );
};

const NotificationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <NotificationSettings onClose={() => navigate("/profile")} />
    </Suspense>
  );
};

const PrivacySettingsPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <PrivacySettings onClose={() => navigate("/profile")} />
    </Suspense>
  );
};

const BalancePage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Balance onClose={() => navigate(-1)} />
    </Suspense>
  );
};

const SupportCenterPage: React.FC = () => {
  const navigate = useNavigate();
  return <SupportCenter onClose={() => navigate("/more")} />;
};

const MessageCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const targetUser = searchParams.get("user");

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <MessageCenter
        onClose={() => navigate(-1)}
        initialUser={targetUser || undefined}
      />
    </Suspense>
  );
};

const NAVBAR_CONFIG: Record<string, boolean> = {
  "": true,
  "/": true,
  "/search": true,
};

const MainLayout: React.FC = () => {
  const location = useLocation();
  const auth = useAuth();
  
  const isAuthenticated = auth.isAuthenticated;
  const showNavbar =
    !!NAVBAR_CONFIG[location.pathname] ||
    location.pathname.startsWith("/video/");

  useEffect(() => {
    console.log(
      "[Navbar Visibility] Pathname:",
      location.pathname,
      "Show Navbar:",
      showNavbar,
    );
  }, [location.pathname, showNavbar]);

  useEffect(() => {
    if (isAuthenticated) {
      document.body.classList.add("user-authenticated");
    } else {
      document.body.classList.remove("user-authenticated");
    }
    return () => {
      document.body.classList.remove("user-authenticated");
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const isProfilePage =
      location.pathname.startsWith("/profile") ||
      location.pathname.startsWith("/user/");
    if (isProfilePage) {
      document.body.classList.add("profile-page-open");
    } else {
      document.body.classList.remove("profile-page-open");
    }
    return () => {
      document.body.classList.remove("profile-page-open");
    };
  }, [location.pathname]);

  useEffect(() => {
    const pauseAllVideos = () => {
      const allVideos = document.querySelectorAll("video");
      allVideos.forEach((video) => {
        if (!video.paused) {
          video.pause();
          video.muted = true;
        }
      });

      const allAudios = document.querySelectorAll("audio");
      allAudios.forEach((audio) => {
        if (!audio.paused) {
          audio.pause();
          audio.muted = true;
        }
      });
    };

    pauseAllVideos();
  }, [location.pathname]);

  return (
    <div className={`app ${isAuthenticated ? "user-authenticated" : ""}`}>
      <GlobalNotificationListener />
      <Suspense fallback={<LoadingSpinner />}>
        <Sidebar />
      </Suspense>
      <div
        className={`app-content ${isAuthenticated ? "user-authenticated" : ""}`}
      >
        {showNavbar && (
          <>
            <Suspense fallback={<div style={{ height: "60px" }}></div>}>
              <Navbar />
            </Suspense>
            <Suspense fallback={null}>
              <SystemNotificationBanner />
            </Suspense>
          </>
        )}
        <div className="app-outlet">
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  const { i18n } = useTranslation();
  return (
  <Routes key={i18n.language}>
    <Route element={<MainLayout />}>
      <Route index element={<HomeFeed />} />
      <Route path="explore" element={<ExplorePage />} />
      <Route path="/video/:id" element={<SingleVideoFeed />} />
      <Route path="/search" element={<SearchResultsPage />} />
      <Route path="live" element={<LivePage />}>
        <Route path="watch/:id" element={<LivePage />} />
      </Route>
      <Route path="more" element={<MorePage />} />

      <Route
        path="following"
        element={
          <ProtectedRoute>
            <FollowingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="watch-later"
        element={
          <ProtectedRoute>
            <WatchLaterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="playlists"
        element={
          <ProtectedRoute>
            <PlaylistsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="qa"
        element={
          <ProtectedRoute>
            <VideoQAPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="upload"
        element={
          <ProtectedRoute>
            <UploadPageRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="user/:username"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="more/studio"
        element={
          <ProtectedRoute>
            <SpryclipsStudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="more/effects"
        element={
          <ProtectedRoute>
            <EffectsLabPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="more/shop"
        element={
          <ProtectedRoute>
            <SellerDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="more/growth-tools"
        element={
          <ProtectedRoute>
            <GrowthToolsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="more/support"
        element={
          <ProtectedRoute>
            <SupportCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="balance"
        element={
          <ProtectedRoute>
            <BalancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="wallet"
        element={
          <ProtectedRoute>
            <BalancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="messages"
        element={
          <ProtectedRoute>
            <MessageCenterPage />
          </ProtectedRoute>
        }
      />
    </Route>

    <Route
      path="/more/business-suite"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <ProtectedRoute>
            <BusinessSuitePage />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path="/more/ad-manager"
      element={
        <ProtectedRoute>
          <AdManagerPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/more/product-catalog"
      element={
        <ProtectedRoute>
          <ProductCatalogPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/more/business-messaging"
      element={
        <ProtectedRoute>
          <BusinessMessagingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/more/activity-center"
      element={
        <ProtectedRoute>
          <ActivityCenterPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/more/notification-settings"
      element={
        <ProtectedRoute>
          <NotificationSettingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/more/privacy-settings"
      element={
        <ProtectedRoute>
          <PrivacySettingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path={ADMIN_ROUTES.LOGIN}
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <AdminLogin />
        </Suspense>
      }
    />
    <Route
      path={ADMIN_ROUTES.DASHBOARD}
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <ProtectedRoute>
            <AdminPanel mode="page" onClose={() => window.history.back()} />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path={ADMIN_ROUTES.POLICY_COMPLIANCE}
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <ProtectedRoute>
            <PolicyCompliance onClose={() => window.history.back()} />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path={ADMIN_ROUTES.SECURITY_SETTINGS}
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <ProtectedRoute>
            <SecuritySettings onClose={() => window.history.back()} />
          </ProtectedRoute>
        </Suspense>
      }
    />
    <Route
      path={ADMIN_ROUTES.NOTIFICATIONS_CONTROL}
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <ProtectedRoute>
            <NotificationsControl onClose={() => window.history.back()} />
          </ProtectedRoute>
        </Suspense>
      }
    />

    <Route
      path="/callback"
      element={
        <div className="loading-container">
          <h3>Processing Login...</h3>
        </div>
      }
    />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
  );
};

const App: React.FC = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <OidcProvider {...oidcConfig}>
        <AuthProvider>
          <SpryAuthProvider config={oidcConfig as any}>
            <NotificationProvider>
              <WatchHistoryProvider>
                <RecommendationProvider>
                  <ErrorToasts />
                  <AppRoutes />
                </RecommendationProvider>
              </WatchHistoryProvider>
            </NotificationProvider>
          </SpryAuthProvider>
        </AuthProvider>
      </OidcProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;